import { AbstractDestroyable, Destroyable, VoidCallback, VoidCallback1 } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Monitor } from '../../../platform/Monitor';
import { KeyExpression } from '../../../platform/input/KeyExpression';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseInput } from '../../../platform/input/MouseInput';
import { WheelInput } from '../../../platform/input/WheelInput';
import { Cursor } from '../Cursor';
import { KeyMappings } from './KeyMappings';
import { ToolBehavior, ToolSettings } from './ToolBehavior';
import { ToolInfo } from './ToolInfo';

type ExcludeLiterals = 'getContextActions' | 'getKeyMappings' | 'getPreferredCursor' | 'init';
type DispatchOption = Exclude<keyof ToolBehavior, keyof Destroyable | ExcludeLiterals>;

export type CompiledKeyMappings = (readonly [KeyExpression, VoidCallback])[];

export class ToolInstance extends AbstractDestroyable {

    public readonly id: string;
    public readonly param: any;
    public state: string;

    private ready = false;

    constructor(
        info: ToolInfo,
        public readonly settings: ToolSettings,
        private behaviors: ToolBehavior[],
        private dispatcher: CommandDispatcher,
        private notifyStateChange: VoidCallback1<string>) {
        super();
        this.id = info.id;
        this.param = info.param;
        this.state = info.state;
    }

    public init(): void {
        const { behaviors } = this;

        let newState = behaviors.reduce((s, tb) => (tb.init(this.settings ?? {}, this.param) || s), this.state);
        if (newState === 'initializing') newState = 'idle';

        if (this.state !== newState) {
            this.changeState(newState);
        }

        this.burden(...behaviors);
        this.burden(() => Cursor.set(null));
        this.ready = true;
    }

    public dispatchEvent(type: string, input: KeyInput | MouseInput | WheelInput): boolean {
        // Don't dispatch if not initialized
        if (!this.ready) return;
        Monitor.logAction(`${this.id}.${type}(${this.state})`);
        let consume = false;
        // Invoke key mapping if keydown
        if (type === 'keydown') {
            const inputExpr = KeyExpression.from(input as KeyInput);
            const mappings = this.behaviors.reduce((ms, tb) => ms.concat(tb.getKeyMappings()), KeyMappings.empty());
            const result = mappings.find(([ex]) => inputExpr.matches(ex));
            if (result) {
                result[1](this.dispatcher);
                consume = true;
            }
        }
        // Invoke dispatch
        const call = dispatchMap[type] as DispatchOption;
        const newState = this.behaviors.reduce((s, tb) => (tb[call](s, input as any) || s), this.state);
        if (this.state !== newState) {
            this.changeState(newState);
            consume = true;
        }
        // Invoke cursor update
        if (input instanceof MouseInput || input instanceof KeyInput) {
            this.changeCursor(newState, input);
        }
        return consume;
    }

    private changeState(newState: string) {
        const oldState = this.state;
        this.state = newState;
        this.behaviors.forEach(x => x.onStateChanged(newState, oldState));
        this.notifyStateChange(newState);
    }

    private changeCursor(newState: string, input: MouseInput | KeyInput) {
        const cursor = this.behaviors.flatMap(x => x.getPreferredCursor(newState, input)).reduce((t, x) => x ?? t, null) || null;
        Cursor.set(cursor as any);
    }
}

const dispatchMap: Record<string, keyof ToolBehavior> = {
    keydown: 'onKeyDown',
    keyup: 'onKeyUp',
    mousedown: 'onMouseDown',
    mousemove: 'onMouseMove',
    mouseup: 'onMouseUp',
    edge: 'onMouseEdge',
    click: 'onClick',
    dblclick: 'onDoubleClick',
    wheel: 'onWheel',
};
